import { sortByAccessor } from 'components/tables/list-table/sortUtil';
import { TimecardTemplateDto } from 'types/timecards';

/**
 * A template with its two classifier codes already resolved to the labels the table prints.
 * Filtering and ordering run on those labels, never on the codes: the user matches and reads
 * "English", not `eng`.
 */
export interface TimecardTemplateRow extends TimecardTemplateDto {
    caseType: string;
    language: string;
}

/** The three fields the filter searches; the two date columns are sort-only. */
const SEARCHED_FIELDS = ['caseType', 'language', 'templateText'] as const;

/** The five sortable columns. Doubles as the whitelist that makes an unknown sort string inert. */
const SORT_FIELDS = ['caseType', 'language', 'templateText', 'validFrom', 'validUntil'] as const;

/**
 * Narrow `rows` to those whose case type, language or template text contains `query`
 * (case-insensitive, trimmed; an empty query keeps everything), then order them by `sort`.
 *
 * `sort` is `sortUtil`'s string encoding — `"<field>,asc" | "<field>,desc" | ""` — which is what
 * `useDataTableSortString` bridges to and from the table header's sorting state, so the header and
 * this helper share one representation. An empty or unrecognised string leaves the server's order
 * alone. Ordering itself is `sortByAccessor`, which drops missing dates to the bottom in both
 * directions.
 */
export function filterAndSortTimecardTemplates(
    rows: TimecardTemplateRow[],
    query: string,
    sort: string
): TimecardTemplateRow[] {
    // `toLowerCase` deliberately, not `toLocaleLowerCase`: under a Turkish locale the latter folds a
    // typed `I` to dotless `ı` while a stored `i` stays `i`, so the query stops matching what it reads.
    const needle = query.trim().toLowerCase();
    const matching = needle
        ? rows.filter((row) => SEARCHED_FIELDS.some((field) => row[field].toLowerCase().includes(needle)))
        : rows;

    const [field, direction] = sort.split(',');
    const sortField = SORT_FIELDS.find((candidate) => candidate === field);
    if (!sortField) {
        return matching;
    }

    return sortByAccessor(matching, (row) => row[sortField], direction === 'desc');
}

/** Rows shown at once. The section pages through the array rather than scrolling it. */
export const TIMECARD_TEMPLATE_PAGE_SIZE = 10;

export interface TimecardTemplatePage {
    /** The rows of the page to render, already filtered and ordered. */
    rows: TimecardTemplateRow[];
    /** The page the slice actually came from: `page`, clamped into range. 1-based. */
    page: number;
    /** How many pages the matching rows fill. At least 1, so "page 1 of 1" holds with no matches. */
    pageCount: number;
}

/**
 * The slice of {@link filterAndSortTimecardTemplates} that page `page` shows.
 *
 * `page` is clamped rather than trusted: a page index outlives the set it indexed — narrowing the
 * filter shrinks the list under a reader who is further down it — and an index past the end would
 * otherwise slice to nothing and read as a broken filter. The caller still sends the reader back to
 * page 1 on a filter or sort change; the clamp is what makes an out-of-range index impossible
 * regardless of which state the caller changed first.
 */
export function pageOfTimecardTemplates(
    rows: TimecardTemplateRow[],
    query: string,
    sort: string,
    page: number
): TimecardTemplatePage {
    const matching = filterAndSortTimecardTemplates(rows, query, sort);
    const pageCount = Math.max(1, Math.ceil(matching.length / TIMECARD_TEMPLATE_PAGE_SIZE));
    const current = Math.min(Math.max(Math.trunc(page), 1), pageCount);
    const start = (current - 1) * TIMECARD_TEMPLATE_PAGE_SIZE;

    return { rows: matching.slice(start, start + TIMECARD_TEMPLATE_PAGE_SIZE), page: current, pageCount };
}
